import type React from 'react';
import { type CSSProperties, useEffect, useState } from 'react';

import Portal from '@/components/Portal/Portal';
import { FullTitlePreviewStyle } from '@/components/Preview/FullTitlePreview';

type Props = {
  isMultiLine?: boolean;
  ref: React.RefObject<HTMLElement>;
  text: string;
  flag?: boolean;
};

/**
 * Returns the average of two numbers.
 *
 * @remarks
 * This method is part of the {@link core-library#Statistics | Statistics subsystem}.
 *
 * @param x - The first input number
 * @param y - The second input number
 * @returns The arithmetic mean of `x` and `y`
 *
 * @beta
 */

const useTitlePreview = ({ isMultiLine = false, ref, text, flag }: Props) => {
  const [isActiveEllipsis, setIsActiveEllipsis] = useState(false);
  const [displayFullTitlePos, setDisplayFullTitlePos] = useState<
    CSSProperties | undefined
  >(undefined);

  useEffect(() => {
    if (!ref || !ref.current) return;

    if (isMultiLine) {
      setIsActiveEllipsis(ref.current.offsetHeight < ref.current.scrollHeight);
    } else {
      setIsActiveEllipsis(ref.current.offsetWidth < ref.current.scrollWidth);
    }
  }, [ref, isMultiLine, flag, text]);

  const handleMouseEnterEvent = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    if (isActiveEllipsis)
      setDisplayFullTitlePos({ left: e.pageX, top: e.pageY });
  };

  const handleMouseLeaveEvent = () => {
    if (isActiveEllipsis) setDisplayFullTitlePos(undefined);
  };
  return {
    isActiveEllipsis,
    displayFullTitlePos,
    handleMouseEnterEvent,
    handleMouseLeaveEvent,
    tooltipNode: (
      <Portal id="preview-layer">
        <FullTitlePreviewStyle style={displayFullTitlePos} status={true}>
          {text}
        </FullTitlePreviewStyle>
      </Portal>
    ),
  };
};

export default useTitlePreview;
